// external
import React, { FC, ReactElement } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';

// internal stuff
import { Page } from './components/protectedRoute';
import './App.css';
import CoeNavbar from './components/coe-navbar/coe-navbar.component';
import HomePage from './pages/home/home.page';
import SettingsPage from './pages/settings/settings.page';
import LoginPage from './pages/login/login.page';
import CreateAccountPage from './pages/create-account/create-account.page';
import NotFoundPage from './pages/404/not-found.page';
import TrainingLogPage from './pages/training-log/training-log.page';
import ForgotPasswordPage from './pages/forgot-password/forgot-password.page';
import ResetPage from './pages/reset/reset.page';
import TeamsPage from './pages/teams/teams.page';
import TeamDetailsPage from './pages/team-details/team-details.page';
import TeamManagementRedirect from './pages/team-details/team-management-redirect';
import PolarOAuthRedirectPage from './pages/registration/polar-oauth-redirect/polar-oauth-redirect.page';
import GarminOAuthRedirectPage from './pages/registration/garmin-oauth-redirect/garmin-oauth-redirect.page';
import SuuntoOAuthRedirectPage from './pages/registration/suunto-oauth-redirect/suunto-oauth-redirect.page';
import StravaOAuthRedirectPage from './pages/registration/strava-oauth-redirect/strava-oauth-redirect.page';
import CorosOAuthRedirectPage from './pages/registration/coros-oauth-redirect/coros-oauth-redirect.page';
import CreateTeamPaymentConfirmationPage from './pages/create-team-payment-confirmation/create-team-payment-confirmation';
import CreateBasicTeamSuccessPage from './pages/create-basic-team-success/create-basic-team-success';
import BillingPage from './pages/billing/billing.page';
import BillingConfirmationPage from './pages/billing/billing-confirmation.page';
import SSOPage from './pages/sso/sso.page';
import EquipmentPage from './pages/equipment/equipment.page';
import EquipmentViewerPage from './pages/equipment-viewer/equipment-viewer.page';
import ChatPage from './pages/chat/chat.page';

const RedirectTeamMessages: FC = () => {
  const { id } = useParams();
  const location = useLocation();
  return <Navigate to={`/teams/${id}/messages${location.search}${location.hash}`} replace />;
};

export interface AppRoute {
  path: string;
  title: string;
  component: ReactElement;
  requiresAuth: boolean;
  navbar?: boolean;
}

function App() {
  const routeConfig: AppRoute[] = [
    {
      path: '/login',
      title: 'Login',
      component: <LoginPage />,
      requiresAuth: false,
    },
    {
      path: '/create-account',
      title: 'Create Account',
      component: <CreateAccountPage />,
      requiresAuth: false,
    },
    {
      path: '/password-reset',
      title: 'Forgot Password',
      component: <ForgotPasswordPage />,
      requiresAuth: false,
    },
    {
      path: '/account/reset/:token',
      title: 'Password Reset',
      component: <ResetPage />,
      requiresAuth: false,
    },
    {
      path: '*',
      title: 'Not Found',
      component: <NotFoundPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/',
      title: 'Home',
      component: <Navigate to="/home" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/home',
      title: 'Home',
      component: <HomePage view="athlete" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/analytics',
      title: 'Analytics',
      component: <Navigate to="/analytics/dashboard" replace />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/analytics/dashboard',
      title: 'Analytics',
      component: <TrainingLogPage tab="dashboard" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/analytics/training-log',
      title: 'Analytics',
      component: <TrainingLogPage tab="training-log" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/analytics/data-info',
      title: 'Analytics',
      component: <TrainingLogPage tab="data-info" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/analytics/raw-data',
      title: 'Analytics',
      component: <TrainingLogPage tab="raw-data" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/equipment',
      title: 'Equipment',
      component: <EquipmentPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/chat',
      title: 'Chat',
      component: <ChatPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams',
      title: 'Teams',
      component: <TeamsPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/create-team',
      title: 'Create Team',
      component: <TeamsPage openCreateTeam />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/create-team/payment-confirmation',
      title: 'Create Team Payment Confirmation',
      component: <CreateTeamPaymentConfirmationPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/create-team/success',
      title: 'Create Team Success',
      component: <CreateBasicTeamSuccessPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/billing',
      title: 'Billing',
      component: <BillingPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/billing/confirmation',
      title: 'Billing Confirmation',
      component: <BillingConfirmationPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/dashboard',
      title: 'Team Dashboard',
      component: <TeamDetailsPage view="dashboard" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/messages',
      title: 'Messages',
      component: <TeamDetailsPage view="messages" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/message-board',
      title: 'Messages',
      component: <RedirectTeamMessages />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-trainings',
      title: 'Team Trainings',
      component: <TeamDetailsPage view="team-trainings" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/athlete-schedules',
      title: 'Athlete Schedules & Training Logs',
      component: <TeamDetailsPage view="athlete-schedules" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/roster',
      title: 'Team Roster',
      component: <TeamDetailsPage view="roster" tab="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/equipment',
      title: 'Team Equipment',
      component: <TeamDetailsPage view="equipment" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/race-log',
      title: 'Race Log',
      component: <TeamDetailsPage view="race-log" tab="profile" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management',
      title: 'Team Roster',
      component: <TeamManagementRedirect to="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/profile',
      title: 'Team Roster',
      component: <TeamManagementRedirect to="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/upgrade',
      title: 'Team Roster',
      component: <TeamManagementRedirect to="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/account',
      title: 'Team Roster',
      component: <TeamManagementRedirect to="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/roster',
      title: 'Team Roster',
      component: <TeamManagementRedirect to="roster" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/groups',
      title: 'Team Training Plan',
      component: <TeamManagementRedirect to="team-trainings" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/custom-forms',
      title: 'Team Training Plan',
      component: <TeamManagementRedirect to="team-trainings" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/teams/:id/team-management/custom-fields',
      title: 'Team Training Plan',
      component: <TeamManagementRedirect to="team-trainings" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/profile',
      title: 'My Dashboard',
      component: <SettingsPage />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/profile/account',
      title: 'Account',
      component: <SettingsPage tab="account" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/profile/guardian-management',
      title: 'Guardian Settings',
      component: <SettingsPage tab="guardian-management" />,
      requiresAuth: true,
      navbar: true,
    },
    {
      path: '/polar/registration',
      title: 'Polar Registration',
      component: <PolarOAuthRedirectPage />,
      requiresAuth: true,
    },
    {
      path: '/garmin/registration',
      title: 'Garmin Registration',
      component: <GarminOAuthRedirectPage />,
      requiresAuth: true,
    },
    {
      path: '/suunto/registration',
      title: 'Suunto Registration',
      component: <SuuntoOAuthRedirectPage />,
      requiresAuth: true,
    },
    {
      path: '/strava/registration',
      title: 'Strava Registration',
      component: <StravaOAuthRedirectPage />,
      requiresAuth: true,
    },
    {
      path: '/coros/registration',
      title: 'COROS Registration',
      component: <CorosOAuthRedirectPage />,
      requiresAuth: true,
    },
    {
      path: '/sso',
      title: 'CoeLab Support SSO',
      component: <SSOPage />,
      requiresAuth: false,
    },
    {
      path: '/equip/:id',
      title: 'Equipment Viewer',
      component: <EquipmentViewerPage />,
      requiresAuth: false,
    },
  ];

  return (
    <>
      <Router>
        <Routes>
          {routeConfig.map((routeData: AppRoute, index: number) => {
            return (
              <Route
                key={index}
                path={routeData.path}
                element={
                  <Page title={routeData.title} protected={routeData.requiresAuth}>
                    {routeData.navbar && <CoeNavbar />}
                    {routeData.component}
                  </Page>
                }
              />
            );
          })}
        </Routes>
      </Router>
    </>
  );
}

export default App;
